import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Dog, DogBreed } from '../models/data.models';
import { normalizeBreedName } from '../models/dog-breed.models';

@Injectable({
    providedIn: 'root'
})
export class DogService {
    private supabase = inject(SupabaseService);
    normalizeBreedName(breed?: string): string {
        return normalizeBreedName(breed);
    }

    async assignResultDog(resultId: string, dogId: string): Promise<void> {
        const { error } = await this.supabase.client.rpc('assign_my_result_dog', {
            p_result_id: resultId, p_dog_id: dogId,
        });
        if (error) throw error;
    }

    async getBorrowedDogIds(): Promise<string[]> {
        const { data, error } = await this.supabase.client.from('profile_borrowed_dogs').select('dog_id');
        if (error) throw error;
        return (data || []).map(row => row.dog_id);
    }

    async setBorrowedDog(profileId: string, dogId: string, borrowed: boolean): Promise<void> {
        const query = this.supabase.client.from('profile_borrowed_dogs');
        const { error } = borrowed
            ? await query.upsert({ profile_id: profileId, dog_id: dogId }, { ignoreDuplicates: true })
            : await query.delete().eq('profile_id', profileId).eq('dog_id', dogId);
        if (error) throw error;
    }

    async getDog(id: string): Promise<Dog | null> {
        const { data, error } = await this.supabase.client
            .from('dogs')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            console.error('Error fetching dog:', error);
            return null;
        }

        return {
            id: data.id,
            ownerId: data.owner_id,
            name: data.name,
            age: data.age,
            birthYear: data.birth_year,
            breed: data.breed,
            gender: data.gender,
            imageUrl: data.image_url
        };
    }

    async getDogBreeds(): Promise<DogBreed[]> {
        const { data, error } = await this.supabase.client
            .from('dog_breeds')
            .select('*')
            .order('name');

        if (error) {
            console.error('Error fetching dog breeds:', error);
            return [];
        }
        return data || [];
    }

    async addDogBreed(breed: Partial<DogBreed>): Promise<DogBreed | null> {
        const { data, error } = await this.supabase.client
            .from('dog_breeds')
            .insert(breed)
            .select()
            .single();

        if (error) {
            console.error('Error adding dog breed:', error);
            return null;
        }
        return data;
    }

    async deleteDogBreed(id: string): Promise<boolean> {
        const { error } = await this.supabase.client
            .from('dog_breeds')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting dog breed:', error);
            return false;
        }
        return true;
    }

    async getDogsByOwner(ownerId: string): Promise<Dog[]> {
        const { data, error } = await this.supabase.client
            .from('dogs')
            .select('*')
            .eq('owner_id', ownerId);

        if (error) {
            console.error('Error fetching owner dogs:', error);
            return [];
        }

        return (data || []).map(d => ({
            id: d.id,
            ownerId: d.owner_id,
            name: d.name,
            age: d.age,
            birthYear: d.birth_year,
            breed: d.breed,
            gender: d.gender,
            imageUrl: d.image_url
        }));
    }

    async updateDog(id: string, dog: Partial<Dog>): Promise<void> {
        const { error } = await this.supabase.client
            .from('dogs')
            .update({
                name: dog.name,
                age: dog.age,
                birth_year: dog.birthYear,
                breed: dog.breed,
                gender: dog.gender,
                image_url: dog.imageUrl
            })
            .eq('id', id);

        if (error) throw error;
    }

    async addDog(dog: Partial<Dog>): Promise<Dog | null> {
        const { data, error } = await this.supabase.client
            .from('dogs')
            .insert({
                owner_id: dog.ownerId,
                name: dog.name,
                age: dog.age,
                birth_year: dog.birthYear,
                breed: dog.breed,
                gender: dog.gender,
                image_url: dog.imageUrl
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    async deleteDog(id: string): Promise<void> {
        const { error } = await this.supabase.client
            .from('dogs')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }

    async mergeDogs(sourceDogId: string, targetDogId: string): Promise<string> {
        const { data, error } = await this.supabase.client.rpc('merge_my_dogs', {
            p_source_dog_id: sourceDogId,
            p_target_dog_id: targetDogId,
        });

        if (error) {
            console.error('Error merging dogs:', error);
            throw error;
        }

        return String(data);
    }

    async mergeDogsAsAdmin(sourceDogId: string, targetDogId: string): Promise<string> {
        const { data, error } = await this.supabase.client.rpc('merge_dogs_as_admin', {
            p_source_dog_id: sourceDogId,
            p_target_dog_id: targetDogId,
        });

        if (error) {
            console.error('Error merging dogs as admin:', error);
            throw error;
        }

        return String(data);
    }
}
